import type { CreateRecordResponse, RecordConflict } from '@konyaxl/shared';
import { ApiRequestError, api } from './api.js';

/**
 * Gönderim kuyruğu.
 *
 * Eşleştirme kaydı kuyruğa atılır ve ekran anında sıfırlanır — operatör
 * sunucuyu beklemez. Ölçümlerde ağ gecikmesi 20-70 ms arasında dalgalanıyor
 * ve kontrol edilemiyor; bu kuyruk sayesinde zamanla yarışan tarafta ağ hiç
 * bulunmuyor.
 *
 * Eski projede her eşleştirmede `load=true` yapılıp iki Firestore sorgusu
 * bekleniyordu (rabiekle.dart:586) — operatör okuma başına ağ gecikmesi
 * kadar duruyordu.
 */

export interface QueueItem {
  clientUuid: string;
  codeA: string;
  codeB: string;
  overwrite: boolean;
  /** Kayda iliştirilen ek alanlar (iade akışında seçilen alt kategori). */
  data?: Record<string, unknown>;
  /** Ekranda göstermek için. */
  queuedAt: number;
}

export interface QueueEvents {
  /** Sunucu kaydı kabul etti. */
  onSuccess: (item: QueueItem, res: CreateRecordResponse) => void;
  /** Sunucu çakışma bildirdi — yerel kontrolün kaçırdığı durum. */
  onConflict: (item: QueueItem, conflict: RecordConflict) => void;
  /** Kalıcı hata (yetki, doğrulama). Kayıt düşürüldü. */
  onError: (item: QueueItem, message: string) => void;
  /** Bekleyen iş sayısı değişti. */
  onPendingChange: (count: number) => void;
}

const STORAGE_KEY = 'konyaxl.queue';

export class SendQueue {
  private items: QueueItem[] = [];
  private running = false;
  private stopped = false;

  constructor(
    private readonly tenantSlug: string,
    private readonly events: QueueEvents,
  ) {
    this.restore();
  }

  get pending(): number {
    return this.items.length;
  }

  /** Kuyruğa ekler ve hemen döner — çağıran taraf beklemez. */
  push(item: QueueItem): void {
    this.items.push(item);
    this.persist();
    this.events.onPendingChange(this.items.length);
    void this.drain();
  }

  stop(): void {
    this.stopped = true;
  }

  /**
   * Kuyruğu sırayla boşaltır. Tek akış hâlinde çalışır: eşleştirmeler
   * okutulma sırasıyla kaydedilir, aksi halde çakışma çözümleri karışabilir.
   */
  private async drain(): Promise<void> {
    if (this.running || this.stopped) return;
    this.running = true;

    while (this.items.length > 0 && !this.stopped) {
      const item = this.items[0]!;

      try {
        const res = await api.post<CreateRecordResponse>(
          `/${this.tenantSlug}/records`,
          {
            codeA: item.codeA,
            codeB: item.codeB,
            clientUuid: item.clientUuid,
            overwrite: item.overwrite,
            data: item.data ?? {},
          },
        );

        this.items.shift();
        this.persist();
        this.events.onPendingChange(this.items.length);
        this.events.onSuccess(item, res);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          if (err.status === 409) {
            this.items.shift();
            this.persist();
            this.events.onPendingChange(this.items.length);
            this.events.onConflict(item, err.body as RecordConflict);
            continue;
          }

          // 4xx kalıcı hatadır; tekrar denemek düzeltmez.
          if (err.status >= 400 && err.status < 500) {
            this.items.shift();
            this.persist();
            this.events.onPendingChange(this.items.length);
            this.events.onError(item, err.message);
            continue;
          }
        }

        /**
         * Ağ hatası veya sunucu hatası: kayıt kuyrukta kalır, bir süre sonra
         * tekrar denenir. Kayıt kaybolmaz — depo WiFi'ı kopsa bile.
         */
        this.running = false;
        setTimeout(() => void this.drain(), 2000);
        return;
      }
    }

    this.running = false;
  }

  /** Kuyruk tarayıcı kapansa da korunur; açılışta kaldığı yerden devam eder. */
  private persist(): void {
    try {
      if (this.items.length === 0) localStorage.removeItem(this.key());
      else localStorage.setItem(this.key(), JSON.stringify(this.items));
    } catch {
      // Depolama dolu olabilir; kuyruk bellekte çalışmaya devam eder.
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(this.key());
      if (!raw) return;
      const parsed = JSON.parse(raw) as QueueItem[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        this.items = parsed;
        void this.drain();
      }
    } catch {
      localStorage.removeItem(this.key());
    }
  }

  private key(): string {
    return `${STORAGE_KEY}.${this.tenantSlug}`;
  }
}
